import { inject, Injectable, Injector, signal } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, Subject } from 'rxjs';
import notify from 'devextreme/ui/notify';
import { NavigationService } from './navigation.service';
import { environment } from '../../environments/environment';

export interface IUser {
  /** Id del Usuario en la DB. Lo necesita la pagina /user-profile para
   *  hacer GET/PUT a /api/usuarios/{id}. Persiste junto con el resto. */
  id?: number;
  email: string;
  name?: string;
  avatarUrl?: string;
  permisos?: string[];
}

export type AuthErrorType = 'credentials' | 'network' | 'server' | 'unknown';

export interface IResponse {
  isOk: boolean;
  data?: IUser;
  message?: string;
  errorType?: AuthErrorType;
}

const defaultPath = '/';

// Espejo de CrmApi.Contracts.Auth.LoginResponse
interface LoginResponse {
  accessToken: string;
  expiresAt: string;        // ISO date
  userId: number;
  userName: string;
  nombreApellido?: string | null;
  permissions: string[];
}

const LS_TOKEN = 'crm.accessToken';
const LS_USER = 'crm.user';
const LS_EXPIRES = 'crm.tokenExpiresAt';
const LS_PERSIST = 'crm.persist';
const HOME_PATH = '/home';

// Devuelve el storage correcto según haya elegido el usuario "Recordarme".
function pickStorage(): Storage {
  return localStorage.getItem(LS_PERSIST) === '1' ? localStorage : sessionStorage;
}

// status 0 → network/CORS/backend caído. 401 → credenciales. 5xx → servidor caído / bug.
// Cualquier otro código que no sea 401 NO debe mostrarse como "usuario/contraseña incorrectos":
// es lo que hacen GitHub/Stripe/Linear — se muestra un banner aparte explicando el problema real.
function classifyAuthError(err: any): IResponse {
  const status: number | undefined = err?.status;
  const serverMsg: string | undefined = err?.error?.detail ?? err?.error?.title;

  // status 0 = no llegó respuesta (backend apagado, DNS fail, CORS preflight, sin red).
  if (!status || status === 0) {
    return {
      isOk: false,
      errorType: 'network',
      message: 'No pudimos conectar con el servidor. Verificá tu conexión y volvé a intentar.',
    };
  }
  if (status === 401) {
    return {
      isOk: false,
      errorType: 'credentials',
      message: serverMsg ?? 'Usuario o contraseña incorrectos.',
    };
  }
  if (status && status >= 500) {
    return {
      isOk: false,
      errorType: 'server',
      message: serverMsg ?? 'El servidor no está respondiendo correctamente. Probá nuevamente en unos minutos.',
    };
  }
  return {
    isOk: false,
    errorType: 'unknown',
    message: serverMsg ?? 'Ocurrió un error inesperado. Volvé a intentarlo.',
  };
}

@Injectable()
export class AuthService {
  private router = inject(Router);
  private http = inject(HttpClient);
  // Injector lazy: NavigationService inyecta AuthService en otros módulos sin riesgo de ciclo.
  private injector = inject(Injector);
  private _user: IUser | null = null;
  // Timers para el aviso proactivo de expiración. Se cancelan en clearSession().
  private _warnTimer: ReturnType<typeof setTimeout> | null = null;
  private _expireTimer: ReturnType<typeof setTimeout> | null = null;
  // Anti-flood: si varias requests fallan con 401 a la vez, una sola notificación.
  private _expiryNotified = false;

  constructor() {
    this.restoreSession();
  }

  private get navigation(): NavigationService {
    return this.injector.get(NavigationService);
  }

  get loggedIn(): boolean {
    return !!this._user && !!this.accessToken;
  }

  /**
   * Stream que emite cuando un login termina exitosamente. Lo usan servicios
   * que necesitan reaccionar al login (ej. `ChatNotificationsService` arranca
   * el hub SignalR — sin esto, el user aparecía offline hasta que entrara
   * manualmente a `/chat`). Hot — el que se suscribe pierde emits anteriores.
   */
  readonly loggedIn$ = new Subject<void>();

  /**
   * Stream que emite cuando el usuario cierra sesión (manual o auto por expirar
   * el JWT). Lo usan servicios que mantienen conexiones vivas — ej.
   * `ChatHubService.stop()` para que el backend marque al user como offline
   * inmediatamente en `PresenceTracker`, en lugar de esperar el timeout de
   * SignalR. Emite ANTES de limpiar el storage para que los suscriptores
   * todavía puedan leer el user actual si lo necesitan.
   */
  readonly loggedOut$ = new Subject<void>();

  /** Usuario logueado actualmente. `null` si no hay sesión. Read-only —
   *  los cambios de sesión se hacen vía `logIn()`/`logOut()`. */
  get user(): IUser | null {
    return this._user;
  }

  get accessToken(): string | null {
    const store = pickStorage();
    const exp = store.getItem(LS_EXPIRES);
    if (exp && Date.parse(exp) <= Date.now()) {
      // Token venció entre requests. Capturamos la URL actual antes de limpiar
      // para que tras el re-login el usuario vuelva exactamente a donde estaba.
      this.handleSessionExpired('silent');
      return null;
    }
    return store.getItem(LS_TOKEN);
  }

  /**
   * Cierra sesión por expiración del token.
   * @param mode 'silent' = sin toast (lo dispara otro callsite), 'notify' = toast.
   */
  private handleSessionExpired(mode: 'silent' | 'notify'): void {
    this.captureCurrentUrl();
    // Mismo evento que el logout manual — para que el hub se cierre y el
    // backend marque al user como offline en PresenceTracker.
    this.loggedOut$.next();
    this.clearSession();
    if (mode === 'notify' && !this._expiryNotified) {
      this._expiryNotified = true;
      notify(
        { message: 'Tu sesión expiró. Iniciá sesión nuevamente.', type: 'warning', displayTime: 4500, position: 'top right', width: 360 },
        'warning',
      );
    }
    // Navegación al sign-in. Si ya estamos en /auth/* no redirigimos para no loopear.
    const url = this.router.url;
    if (!url.startsWith('/auth/')) {
      this.router.navigate(['/auth/sign-in']);
    }
  }

  /** Marca la URL actual como `intendedUrl` salvo que ya estemos en /auth/*. */
  private captureCurrentUrl(): void {
    const url = this.router.url;
    if (url && !url.startsWith('/auth/') && url !== '/') {
      this.intendedUrl = url;
    }
  }

  /** API pública para que el interceptor dispare el flujo de expiración con toast. */
  notifySessionExpired(): void {
    this.handleSessionExpired('notify');
  }

  /**
   * Programa un aviso proactivo ~2 min antes de la expiración real.
   * El usuario ve un toast de advertencia y puede guardar lo que esté haciendo
   * antes del 401 abrupto. Si el token expira durante el toast, se dispara
   * el flujo normal de expiración.
   */
  private scheduleExpiryWarnings(expiresAtIso: string): void {
    this.clearExpiryTimers();
    const expiresAt = Date.parse(expiresAtIso);
    if (Number.isNaN(expiresAt)) return;

    const now = Date.now();
    const msToExpire = expiresAt - now;
    const WARN_LEAD_MS = 2 * 60 * 1000;
    const msToWarn = msToExpire - WARN_LEAD_MS;

    if (msToWarn > 0) {
      this._warnTimer = setTimeout(() => {
        // Abre el modal de inactividad: el countdown empieza ahora, llega a 0
        // al `msToExpire`. El user puede clickear "Continuar" para refrescar
        // el token (llama `/api/auth/extend`) o "Cerrar sesión" para logout
        // manual. Si el countdown llega a 0 sin acción, el `_expireTimer` de
        // abajo dispara `handleSessionExpired('notify')`.
        const secs = Math.max(1, Math.floor((expiresAt - Date.now()) / 1000));
        this.expiryModal.set({ segundosRestantes: secs });
      }, msToWarn);
    }
    // Cuando llega el momento exacto de expiración, ejecutamos el flujo limpio
    // sin esperar al primer 401 — evita que el usuario haga click en algo y
    // reciba un error feo.
    if (msToExpire > 0) {
      this._expireTimer = setTimeout(() => {
        this.expiryModal.set(null);
        this.handleSessionExpired('notify');
      }, msToExpire);
    }
  }

  /**
   * Estado del modal de "tu sesión está por expirar". null = oculto.
   * Cuando tiene valor, el componente `ExpiryModalComponent` lo renderiza con
   * countdown decreasing y botones "Continuar" / "Cerrar sesión".
   */
  readonly expiryModal = signal<{ segundosRestantes: number } | null>(null);

  /**
   * Llama al backend para renovar el JWT actual. Si OK, persiste el token
   * nuevo + reprograma los timers. Si falla (user dado de baja, etc.),
   * cae al `handleSessionExpired` que vacía la sesión.
   */
  async extenderSesion(): Promise<boolean> {
    try {
      const res = await firstValueFrom(
        this.http.post<LoginResponse>(`${environment.apiUrl}/auth/extend`, {}),
      );
      const user: IUser = {
        id: res.userId,
        email: res.userName ?? this._user?.email ?? '',
        name: res.nombreApellido ?? res.userName,
        permisos: res.permissions ?? [],
      };
      const store = pickStorage();
      store.setItem(LS_TOKEN, res.accessToken);
      store.setItem(LS_USER, JSON.stringify(user));
      store.setItem(LS_EXPIRES, res.expiresAt);
      this._user = user;
      this._expiryNotified = false;
      this.expiryModal.set(null);
      this.scheduleExpiryWarnings(res.expiresAt);
      return true;
    } catch {
      this.expiryModal.set(null);
      this.handleSessionExpired('notify');
      return false;
    }
  }

  private clearExpiryTimers(): void {
    if (this._warnTimer) { clearTimeout(this._warnTimer); this._warnTimer = null; }
    if (this._expireTimer) { clearTimeout(this._expireTimer); this._expireTimer = null; }
  }

  private _lastAuthenticatedPath: string = defaultPath;

  set lastAuthenticatedPath(value: string) {
    this._lastAuthenticatedPath = value;
  }

  // URL que el usuario intentó visitar antes de ser redirigido al login.
  // El AuthGuard la setea, AuthService.logIn la consume tras autenticar.
  intendedUrl: string | null = null;

  hasPermission(claim: string): boolean {
    return !!this._user?.permisos?.includes(claim);
  }

  /**
   * Versión del set de claims que el backend emitió en este JWT.
   * El backend la emite como `auth:claims-version=YYYY-MM-DD`. Si el JWT es
   * viejo (de antes que el backend agregara este claim), devuelve null y el
   * frontend muestra un toast pidiendo re-login para refrescar permisos.
   */
  get claimsVersion(): string | null {
    const claim = this._user?.permisos?.find(p => p.startsWith('auth:claims-version='));
    return claim ? claim.split('=')[1] : null;
  }

  /**
   * `true` si el JWT en sesión tiene una versión de claims menor a la que el
   * frontend espera. Se usa para mostrar el toast "tu sesión está
   * desactualizada — re-loguéate para acceder a las nuevas funcionalidades".
   */
  esJwtViejo(): boolean {
    // Si tiene cualquier permiso = ya está logueado. Si NO tiene la version
    // o tiene una < esperada, es viejo. La fecha la sincronizamos con el
    // backend (`JwtTokenIssuer.CurrentClaimsVersion`) — cambiar acá cuando
    // se incremente allá.
    if (!this._user?.permisos?.length) return false;
    const EXPECTED = '2026-05-16-v2';
    const actual = this.claimsVersion;
    return !actual || actual < EXPECTED;
  }

  async logIn(email: string, password: string, rememberMe = false): Promise<IResponse> {
    try {
      const res = await firstValueFrom(
        this.http.post<LoginResponse>(`${environment.apiUrl}/auth/login`, { username: email, password, rememberMe })
      );

      const user: IUser = {
        id: res.userId,
        email: res.userName ?? email,
        name: res.nombreApellido ?? res.userName,
        permisos: res.permissions ?? [],
      };

      // El flag persist va siempre en localStorage para que pickStorage() pueda decidir
      // dónde leer/escribir las otras claves. Las claves "session" se borran al cerrar.
      this.clearSession();
      if (rememberMe) {
        localStorage.setItem(LS_PERSIST, '1');
      } else {
        localStorage.removeItem(LS_PERSIST);
      }
      const store = pickStorage();
      store.setItem(LS_TOKEN, res.accessToken);
      store.setItem(LS_USER, JSON.stringify(user));
      store.setItem(LS_EXPIRES, res.expiresAt);
      this._user = user;
      this._expiryNotified = false;
      this.scheduleExpiryWarnings(res.expiresAt);

      // Notificá a quien necesite reaccionar al login (ej. arrancar hub SignalR
      // del chat para marcar presence online antes de entrar al modulo).
      this.loggedIn$.next();

      // Reset y carga del menu del usuario. Encerrado en try/catch para que un fallo
      // del menu no aborte el flow de login (tendríamos token pero no redirigiríamos).
      try {
        this.navigation.clear();
        void this.navigation.load();
      } catch (navErr) {
        console.warn('No se pudo cargar el menu tras el login', navErr);
      }

      // Post-login: SIEMPRE va a /home. HomeComponent decide qué mostrar
      // según el rol (admins → dashboard, clientes/operadores → home personalizado).
      const target = this.intendedUrl && this.intendedUrl !== '/'
        ? this.intendedUrl
        : HOME_PATH;
      this.intendedUrl = null;
      // Esperamos la navegacion (no fire-and-forget). En IIS prod, /home es un
      // lazy chunk que tarda 100-500ms en bajar la primera vez; sin el await el
      // login resolvia inmediatamente y el componente quitaba el spinner antes
      // que el router montara /home → "flash" del form sin spinner durante el gap.
      await this.router.navigateByUrl(target);
      return { isOk: true, data: user };
    } catch (err: any) {
      console.error('Login fallo:', err);
      return classifyAuthError(err);
    }
  }

  async getUser(): Promise<IResponse> {
    return { isOk: !!this._user, data: this._user ?? undefined };
  }

  async createAccount(email: string, password: string): Promise<IResponse> {
    return { isOk: false, message: 'No habilitado' };
  }

  async changePassword(email: string, recoveryCode: string): Promise<IResponse> {
    return { isOk: false, message: 'No habilitado' };
  }

  async resetPassword(email: string): Promise<IResponse> {
    return { isOk: false, message: 'No habilitado' };
  }

  async logOut() {
    // Emitir ANTES de limpiar para que los suscriptores (ChatHub, push, etc.)
    // alcancen a usar el token todavía válido para cerrar conexiones limpias.
    this.loggedOut$.next();
    this.clearSession();
    this.navigation.clear();

    // Limpiar state de usuario: localStorage (pinned, recents, saved views,
    // comments mock, grid state) + signals in-memory de los services.
    // Resuelto lazy via Injector para evitar circular import: AuthService no
    // importa el cleanup service en top, lo obtiene cuando se ejecuta logout.
    // Si por algún motivo el service no se puede resolver (ej. no disponible
    // en este injector context), seguimos con el logout — el storage queda con
    // basura pero el usuario igual sale de la sesión.
    try {
      const { UserSessionCleanupService } = await import('../shared/services/user-session-cleanup.service');
      this.injector.get(UserSessionCleanupService).cleanup();
    } catch (err) {
      console.warn('[logOut] No se pudo limpiar storage/state de usuario:', err);
    }

    this.router.navigate(['/auth/sign-in']);
  }

  private restoreSession() {
    const store = pickStorage();
    const token = store.getItem(LS_TOKEN);
    const userJson = store.getItem(LS_USER);
    const exp = store.getItem(LS_EXPIRES);
    if (!token || !userJson || !exp) return;
    if (Date.parse(exp) <= Date.now()) {
      this.clearSession();
      return;
    }
    try {
      this._user = JSON.parse(userJson);
      // Reanudamos los avisos sobre la expiración persistida (no la del login original).
      this.scheduleExpiryWarnings(exp);
    } catch {
      this.clearSession();
    }
  }

  private clearSession() {
    // Limpiamos en ambos storages para no dejar restos.
    for (const s of [localStorage, sessionStorage]) {
      s.removeItem(LS_TOKEN);
      s.removeItem(LS_USER);
      s.removeItem(LS_EXPIRES);
    }
    localStorage.removeItem(LS_PERSIST);
    this._user = null;
    this.clearExpiryTimers();
  }
}

@Injectable()
export class AuthGuardService implements CanActivate {
  private router = inject(Router);
  private authService = inject(AuthService);

  canActivate(
    route: ActivatedRouteSnapshot,
    state?: any
  ): boolean {
    const isLoggedIn = this.authService.loggedIn;
    const isAuthForm = [
      'sign-in',
      'reset-password',
      'register',
      'change-password/:recoveryCode',
    ].includes(route.routeConfig?.path || defaultPath);

    if (!isLoggedIn && isAuthForm) {
      return true;
    }
    if (!isLoggedIn) {
      // Capturamos la URL completa que el usuario intentó (con query/fragments) para
      // redirigir despues del login.
      const url: string | undefined = state?.url;
      if (url && !url.startsWith('/auth/')) {
        this.authService.intendedUrl = url;
      }
      this.router.navigate(['/auth/sign-in']);
      return false;
    }
    if (isAuthForm) {
      this.router.navigate([HOME_PATH]);
      return false;
    }

    this.authService.lastAuthenticatedPath = route.routeConfig?.path || defaultPath;
    return true;
  }
}

/**
 * Guard que protege rutas exclusivas de administradores. El criterio "admin"
 * es el mismo del backend: tener el claim `cliente:viewall` (emitido solo
 * cuando el usuario tiene Rol_Id == 1, ver `JwtTokenIssuer.cs`).
 */
@Injectable()
export class AdminGuardService implements CanActivate {
  private router = inject(Router);
  private authService = inject(AuthService);

  canActivate(): boolean {
    if (!this.authService.loggedIn) {
      this.router.navigate(['/auth/sign-in']);
      return false;
    }
    if (!this.authService.hasPermission('cliente:viewall')) {
      // Operadores y clientes ven la lista de clientes como home alternativo.
      this.router.navigate(['/clientes']);
      return false;
    }
    return true;
  }
}
